import "./tokens.css";
import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Router } from "@lit-labs/router";
import { routes, fallback } from "./router.js";
import { bindRouter, navigate } from "./nav.js";
import { pb } from "./api/client.js";
import { shared } from "./styles.js";
import { searchIcon } from "./lib/icons.js";
import "./components/theme-toggle.js";
import "./components/account-menu.js";

/**
 * registry-app is the application shell: a sticky top bar (cyan brand glyph, tab
 * nav / breadcrumb, a ⌘K search pill, auth, theme toggle) and the routed
 * outlet. It owns the single top-level Router, which intercepts in-app link clicks
 * and browser navigation and re-renders the outlet — and this shell — on every route
 * change, so the active tab / breadcrumb derive from location at render time.
 */
@customElement("registry-app")
export class RegistryApp extends LitElement {
  static override styles = [
    shared,
    css`
      :host {
        display: block;
        min-height: 100vh;
        background: var(--surface);
      }
      header {
        position: sticky;
        top: 0;
        z-index: 20;
        background: var(--surface);
        border-bottom: 1px solid var(--border);
      }
      .bar {
        display: flex;
        align-items: center;
        gap: 18px;
        height: 52px;
        max-width: 1240px;
        margin: 0 auto;
        padding: 0 24px;
      }
      .brand {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .brand:hover {
        text-decoration: none;
      }
      .glyph {
        width: 22px;
        height: 22px;
        border-radius: 6px;
        background: var(--brand);
        color: var(--brand-contrast);
        display: flex;
        align-items: center;
        justify-content: center;
        font: 800 13px/1 var(--display);
      }
      .brand-label {
        font: 600 14.5px/1 var(--sans);
        color: var(--text);
        white-space: nowrap;
      }
      nav.tabs {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      .tab {
        font: 500 13px/1 var(--sans);
        color: var(--text-dim);
        padding: 6px 11px;
        border-radius: 7px;
      }
      .tab:hover {
        text-decoration: none;
        color: var(--text);
      }
      .tab.active {
        color: var(--text);
        font-weight: 600;
        background: var(--surface-3);
      }
      .crumbs {
        font: 400 13px/1 var(--mono);
        color: var(--text-faint);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .crumbs a {
        color: var(--text-faint);
      }
      .crumbs .here {
        color: var(--text);
      }
      .spacer {
        flex: 1;
      }
      .search {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 32px;
        width: 240px;
        padding: 0 11px;
        border: 1px solid var(--border);
        border-radius: 8px;
        background: var(--surface-2);
        color: var(--text-faint);
      }
      .search:focus-within {
        border-color: var(--brand);
      }
      .search input {
        flex: 1;
        min-width: 0;
        width: auto;
        background: transparent;
        border: 0;
        padding: 0;
        font: 400 12.5px/1 var(--sans);
        color: var(--text);
      }
      .search input:focus {
        border: 0;
        outline: none;
      }
      .kbd {
        font: 500 11px/1 var(--mono);
        background: var(--surface);
        border: 1px solid var(--border);
        border-radius: 4px;
        padding: 3px 5px;
        color: var(--text-faint);
      }
      .right {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .link-sign {
        font: 500 13px/1 var(--sans);
        color: var(--text-dim);
      }
      /* Phone: wrap the bar into two rows — brand + search + actions on top,
         tabs / breadcrumb on a full-width second row (flex order re-slots them). */
      @media (max-width: 720px) {
        .bar {
          flex-wrap: wrap;
          height: auto;
          padding: 8px 16px;
          gap: 10px;
          row-gap: 8px;
        }
        .search {
          order: 1;
          flex: 1;
          width: auto;
          min-width: 0;
        }
        .search input {
          font-size: 16px;
        }
        .kbd {
          display: none;
        }
        .right {
          order: 2;
        }
        nav.tabs,
        .crumbs {
          order: 3;
          flex-basis: 100%;
        }
        nav.tabs {
          overflow-x: auto;
        }
        nav.tabs .tab {
          padding: 6px 8px;
          white-space: nowrap;
        }
        .crumbs {
          overflow-x: auto;
          text-overflow: clip;
        }
      }
      @media (max-width: 600px) {
        .brand-label {
          display: none;
        }
      }
    `,
  ];

  private router = new Router(this, routes, { fallback });
  @state() private authVersion = 0;
  private unsub?: () => void;
  private onKeydown = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
      e.preventDefault();
      this.renderRoot.querySelector<HTMLInputElement>(".search input")?.focus();
    }
  };

  override connectedCallback() {
    super.connectedCallback();
    bindRouter(this.router);
    this.unsub = pb.authStore.onChange(() => (this.authVersion += 1));
    window.addEventListener("keydown", this.onKeydown);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.unsub?.();
    window.removeEventListener("keydown", this.onKeydown);
  }

  private onSearch(e: Event) {
    const q = (e.target as HTMLInputElement).value;
    navigate("/?q=" + encodeURIComponent(q));
  }

  private currentQuery(): string {
    return new URLSearchParams(location.search).get("q") ?? "";
  }

  /** breadcrumb returns segments for /p/:scope/:name and /scopes/:scope, else null. */
  private breadcrumb(): { href: string; label: string; here?: boolean }[] | null {
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts[0] === "p" && parts[1] && parts[2]) {
      return [
        { href: `/scopes/${parts[1]}`, label: `@${parts[1]}` },
        { href: `/p/${parts[1]}/${parts[2]}`, label: parts[2], here: true },
      ];
    }
    if (parts[0] === "scopes" && parts[1]) {
      return [{ href: `/scopes/${parts[1]}`, label: `@${parts[1]}`, here: true }];
    }
    return null;
  }

  private renderTabs() {
    const onHome = location.pathname === "/";
    const sort = new URLSearchParams(location.search).get("sort") ?? "";
    const explore = onHome && sort !== "pulls" && sort !== "updated";
    const trending = onHome && sort === "pulls";
    const fresh = onHome && sort === "updated";
    return html`<nav class="tabs" aria-label="Browse">
      <a class="tab ${explore ? "active" : ""}" href="/">Explore</a>
      <a class="tab ${trending ? "active" : ""}" href="/?sort=pulls">Trending</a>
      <a class="tab ${fresh ? "active" : ""}" href="/?sort=updated">New</a>
    </nav>`;
  }

  private renderCrumbs(crumbs: { href: string; label: string; here?: boolean }[]) {
    return html`<span class="crumbs"
      >/
      ${crumbs.map(
        (c, i) =>
          html`${i > 0 ? " / " : " "}<a class=${c.here ? "here" : ""} href=${c.href}>${c.label}</a>`,
      )}</span
    >`;
  }

  override render() {
    const authed = pb.authStore.isValid;
    const crumbs = this.breadcrumb();
    return html`
      <header>
        <div class="bar">
          <a class="brand" href="/" aria-label="bino Registry home">
            <span class="glyph" aria-hidden="true">b</span>
            <span class="brand-label">Registry</span>
          </a>
          ${crumbs ? this.renderCrumbs(crumbs) : this.renderTabs()}
          <span class="spacer"></span>
          <div class="search" role="search">
            ${searchIcon(14)}
            <input
              type="search"
              aria-label="Search the registry"
              placeholder="Search the registry"
              .value=${this.currentQuery()}
              @input=${this.onSearch}
            />
            <span class="kbd" aria-hidden="true">⌘K</span>
          </div>
          <div class="right">
            <theme-toggle></theme-toggle>
            ${authed
              ? html`<account-menu></account-menu>`
              : html`<a class="link-sign" href="/login">Sign in</a>`}
          </div>
        </div>
      </header>
      <main>${this.router.outlet()}</main>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "registry-app": RegistryApp;
  }
}
